<!-- ❗Errors in the form are set on line 60 -->
<script setup lang="ts">
import { VForm } from 'vuetify/components/VForm'
import authV2LoginIllustration from '@images/pages/loginback.jpg'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import { signIn } from '@/plugins/auth/msalConfig'
import { PermissionsLoadedKey, loadAbilityRules } from '@/plugins/casl/permissions'

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const isLoading = ref(false)

const route = useRoute()
const router = useRouter()

const ability = useAbility()

const errors = ref<Record<string, string | undefined>>({
  email: undefined,
  password: undefined,
})

const refVForm = ref<VForm>()

const permissionsError = ref<string | null>(null)

const login = async () => {
  errors.value = {}
  permissionsError.value = null
  isLoading.value = true

  try {
    const msalAccount = await signIn()
    if (msalAccount) {
      // DAY-37: only the menus and pages the user's roles allow. Without them, don't go in
      // with an empty menu: say so and let the user try again.
      const userAbilityRules = await loadAbilityRules()
      if (!userAbilityRules) {
        permissionsError.value = 'We could not load your permissions. Please try again.'

        return
      }

      const userData = {
        username: msalAccount.name,
        role: 'admin',
      }

      useCookie('userAbilityRules').value = JSON.stringify(userAbilityRules)
      ability.update(userAbilityRules)
      sessionStorage.setItem(PermissionsLoadedKey, '1')

      useCookie('userData').value = JSON.stringify(userData)
      useCookie('accessToken').value = msalAccount.idToken
    }

    await nextTick(() => {
      router.replace(route.query.to ? String(route.query.to) : '/')
    })
  } catch (err) {
    console.error(err)

    setTimeout(() => {
      errors.value = {}
    }, 1000)
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <RouterLink to="/">
    <div class="auth-logo d-flex align-center gap-x-2">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ themeConfig.app.title }}
      </h1>
    </div>
  </RouterLink>

  <VRow no-gutters class="auth-wrapper bg-surface">
    <VCol md="8" class="d-none d-md-flex">
      <div class="position-relative bg-background w-100 pa-8">
        <div class="d-flex align-center justify-center w-100 h-100">
          <VImg cover height="100%" :src="authV2LoginIllustration" class="auth-illustration" />
        </div>
      </div>
    </VCol>

    <VCol cols="12" md="4" class="auth-card-v2 d-flex align-center justify-center">
      <VCard flat :max-width="500" class="mt-12 mt-sm-0 pa-6">
        <VCardText>
          <h4 class="text-h4 mb-1">
            Welcome to <span class="text-capitalize"> {{ themeConfig.app.title }} </span>! 👋🏻
          </h4>
          <p class="mb-0">Please sign-in to your account and start the adventure</p>
        </VCardText>

        <VCardText>
          <VForm ref="refVForm" @submit.prevent="login">
            <VRow>
              <VCol v-if="permissionsError" cols="12">
                <VAlert type="error" variant="tonal" density="compact">
                  {{ permissionsError }}
                </VAlert>
              </VCol>
              <VCol cols="12">
                <VBtn block type="submit" :loading="isLoading"> <VIcon start icon="mdi-microsoft" /> Login </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use '@core/scss/template/pages/page-auth';
</style>
